﻿import React, { useMemo } from 'react';
import {
  makeStyles,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  IconButton,
  Hidden,
} from '@material-ui/core';
import { format } from 'date-fns';
import filesize from 'filesize';

import { truncate } from '../../utils/truncate';
import UploadingIconSvg from '../svg/UploadingIconSvg';
import DeleteIcon from '../svg/DeleteIcon';
import DownloadFileArrowIcon from '../svg/DownloadFileArrowIcon';
import { STANDARD_DATE_FORMAT } from '../../constants';
import DocumentFileIcon from './DocumentFileIcon';

const useStyles = makeStyles((theme) => ({
  fileIcon: {
    height: '25px !important',
    width: '17px !important',
    marginTop: theme.spacing(1.2),
  },

  listIcon: {
    minWidth: '30px !important',
    marginBottom: 'auto',
  },
  listItem: {
    '& .MuiListItemText-primary': {
      fontSize: '12px !important',
      fontWeight: 500,
    },
    overflowWrap: 'anywhere',
    paddingBottom: 0,
  },
  deleteIcon: {
    height: 15,
    width: 15,
  },
  secondaryText: {
    color: theme.palette.grey[1000],
    fontWeight: '400',
  },
  secondaryTextError: {
    color: theme.palette.error.main,
    fontWeight: '400',
  },
  downloadIcon: {
    marginRight: theme.spacing(2),
  },
  listItemFileNameMdUp: {
    marginTop: 0,
    marginRight: theme.spacing(1),
  },
  listItemFileNameSmDown: {
    marginTop: 0,
    marginBottom: 0,
  },
  list: { paddingBottom: 0 },
}));

const FileListItem = (props) => {
  const { file, onDelete, onDownload, showDelete, showDownload } = props;
  const classes = useStyles();

  let secondaryText;
  if (file.error) {
    secondaryText = (
      <Typography variant="h6" className={classes.secondaryTextError}>
        {file.error}
      </Typography>
    );
  } else if (file.isNameOnly) {
    secondaryText = '';
  } else {
    secondaryText = (
      <Typography variant="h6" className={classes.secondaryText}>
        {file.uploaded
          ? `${format(new Date(file.timestamp), STANDARD_DATE_FORMAT)}, ${filesize(file.size, { spacer: '' })}`
          : 'Uploading...'}
      </Typography>
    );
  }
  return (
    <ListItem
      className={classes.listItem}
      disableGutters
      title={file.name}
      data-testid="uia-fileItem"
    >
      <ListItemIcon className={classes.listIcon}>
        <DocumentFileIcon fileName={file.name} className={classes.fileIcon} />
      </ListItemIcon>
      <Hidden mdUp>
        <ListItemText
          primary={truncate(file.name, 25)}
          secondary={secondaryText}
          className={classes.listItemFileNameMdUp}
        />
      </Hidden>
      <Hidden smDown>
        <ListItemText
          primary={truncate(file.name, 50)}
          secondary={secondaryText}
          className={classes.listItemFileNameSmDown}
        />
      </Hidden>
      {(showDownload || showDelete) && (
        <ListItemSecondaryAction>
          {file.uploaded || file.error ? (
            <React.Fragment>
              {showDownload && (
                <IconButton
                  edge="end"
                  aria-label="download"
                  data-testid="download-file"
                  title="Download"
                  onClick={onDownload}
                >
                  <DownloadFileArrowIcon className={classes.downloadIcon} />
                </IconButton>
              )}
              {showDelete && (
                <IconButton
                  edge="end"
                  aria-label="delete"
                  data-testid="delete-file"
                  onClick={onDelete}
                  title="Delete"
                >
                  <DeleteIcon className={classes.deleteIcon} />
                </IconButton>
              )}
            </React.Fragment>
          ) : (
            <UploadingIconSvg />
          )}
        </ListItemSecondaryAction>
      )}
    </ListItem>
  );
};

const FileList = (props) => {
  const { files, onDelete, onDownload, showDelete = true, showDownload = true } = props;
  const classes = useStyles();
  const sortedFiles = useMemo(
    () =>
      files
        .map((file, index) => ({
          ...file,
          realIndex: index,
        }))
        .sort((a, b) => {
          if (!a.uploaded && b.uploaded) {
            return -1;
          }
          if (a.uploaded && !b.uploaded) {
            return 1;
          }
          if (a.name < b.name) {
            return -1;
          }
          if (a.name > b.name) {
            return 1;
          }
          return 0;
        }),
    [files],
  );

  return (
    <List component="nav" className={classes.list} data-testid="uia-filelist">
      {sortedFiles.map(
        (file) =>
          file && (
            <FileListItem
              file={file}
              error={file.error}
              onDelete={() => onDelete(file.realIndex)}
              onDownload={() => onDownload(file.realIndex)}
              key={file.name}
              showDelete={showDelete}
              showDownload={showDownload}
            />
          ),
      )}
    </List>
  );
};

export default FileList;
